import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Check, Copy, Smartphone, Loader2, PartyPopper, AlertCircle, MapPin } from "lucide-react";
import { api, fmtINR, pkgImage } from "@/lib/api";
import { useLang, pick } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const todayStr = () => new Date().toISOString().split("T")[0];

export default function BookingFlow() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const navigate = useNavigate();

  const [pkg, setPkg] = useState(null);
  const [step, setStep] = useState(1);
  const [date, setDate] = useState("");
  const [checking, setChecking] = useState(false);
  const [avail, setAvail] = useState(null);
  const [form, setForm] = useState({ customer_name: "", customer_email: "", customer_phone: "", address: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [payment, setPayment] = useState(null);
  const [utr, setUtr] = useState("");

  useEffect(() => {
    api.get(`/packages/${slug}`).then(({ data }) => setPkg(data)).catch(() => {
      toast.error("Package not found");
      navigate("/packages");
    });
  }, [slug]);

  if (!pkg) return <div className="min-h-[60vh] flex items-center justify-center text-gold">Loading…</div>;

  const advance = Math.round((pkg.price * (pkg.deposit_percent || 10)) / 100);
  const balance = pkg.price - advance;

  const checkDate = async () => {
    if (!date) return toast.error("Please select a date");
    setChecking(true);
    try {
      const { data } = await api.get(`/availability`, { params: { date } });
      setAvail(data);
      if (data.available) toast.success(t("date_available"));
      else toast.error(data.reason || t("date_unavailable"));
    } finally {
      setChecking(false);
    }
  };

  const submitBooking = async () => {
    if (!form.customer_name || !form.customer_email || !form.customer_phone)
      return toast.error("Please fill name, email and phone");
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", { package_id: pkg.id, event_date: date, ...form });
      setPayment(data);
      setStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create booking");
    } finally {
      setSubmitting(false);
    }
  };

  const submitRef = async () => {
    if (!utr.trim()) return toast.error("Enter the UPI reference / transaction ID");
    setSubmitting(true);
    try {
      await api.post(`/bookings/${payment.booking_number}/payment-ref`, { utr, contact: form.customer_email });
      setStep(4);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit reference");
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [t("select_date"), t("your_details"), t("payment"), "Done"];

  return (
    <div className="max-w-5xl mx-auto px-5 lg:px-8 py-14">
      <div className="mb-10">
        <Link to="/packages" className="text-sm text-zinc-600 hover:text-gold">← {t("nav_packages")}</Link>
        <h1 className="font-display text-3xl sm:text-4xl text-zinc-900 mt-3">{pick(pkg, lang, "name")}</h1>
      </div>

      {/* Stepper */}
      <div className="flex items-center justify-between mb-10 max-w-xl">
        {steps.map((s, i) => {
          const n = i + 1;
          const active = step >= n;
          return (
            <React.Fragment key={i}>
              <div className="flex flex-col items-center gap-2">
                <div className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold ${active ? "gold-gradient text-white" : "bg-secondary text-zinc-600"}`}>
                  {step > n ? <Check size={16} /> : n}
                </div>
                <span className={`text-[11px] ${active ? "text-gold" : "text-zinc-500"}`}>{s}</span>
              </div>
              {i < steps.length - 1 && <div className={`flex-1 h-px mx-2 ${step > n ? "bg-gold/60" : "bg-white/10"}`} />}
            </React.Fragment>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          {step === 1 && (
            <div className="bg-card border border-pink-200 rounded-2xl p-6 sm:p-8">
              <h2 className="font-display text-2xl text-zinc-900 flex items-center gap-2"><CalendarDays className="text-gold" size={22} /> {t("select_date")}</h2>
              <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-gold bg-pink-100 border border-pink-200 rounded-full px-3 py-1.5 w-fit" data-testid="booking-service-area">
                <MapPin size={13} className="shrink-0" /> {t("service_area")}
              </p>
              <div className="mt-6 space-y-4">
                <div>
                  <Label className="text-zinc-700">{t("event_date")}</Label>
                  <Input
                    type="date"
                    min={todayStr()}
                    value={date}
                    data-testid="booking-date-input"
                    onChange={(e) => { setDate(e.target.value); setAvail(null); }}
                    className="mt-2 bg-white border-pink-200 text-zinc-900 [color-scheme:light]"
                  />
                </div>
                <Button onClick={checkDate} disabled={checking} data-testid="check-availability-btn" variant="outline" className="border-gold/40 text-gold hover:bg-gold/10">
                  {checking ? <Loader2 className="animate-spin mr-2" size={16} /> : null} {t("check_availability")}
                </Button>
                {avail && (
                  <div data-testid="availability-result" className={`flex items-center gap-2 text-sm rounded-lg p-3 ${avail.available ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"}`}>
                    {avail.available ? <Check size={16} /> : <AlertCircle size={16} />}
                    {avail.available ? t("date_available") : avail.reason}
                  </div>
                )}
                <Button onClick={() => setStep(2)} disabled={!avail?.available} data-testid="step1-continue-btn" className="gold-gradient text-white font-semibold rounded-full w-full sm:w-auto px-8">
                  {t("continue")}
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="bg-card border border-pink-200 rounded-2xl p-6 sm:p-8">
              <h2 className="font-display text-2xl text-zinc-900">{t("your_details")}</h2>
              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-700">{t("full_name")} *</Label>
                  <Input data-testid="input-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="mt-2 bg-white border-pink-200 text-zinc-900" />
                </div>
                <div>
                  <Label className="text-zinc-700">{t("phone")} *</Label>
                  <Input data-testid="input-phone" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="mt-2 bg-white border-pink-200 text-zinc-900" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-zinc-700">{t("email")} *</Label>
                  <Input data-testid="input-email" type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="mt-2 bg-white border-pink-200 text-zinc-900" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-zinc-700">{t("event_address")}</Label>
                  <Input data-testid="input-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-2 bg-white border-pink-200 text-zinc-900" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-zinc-700">{t("notes")}</Label>
                  <Textarea data-testid="input-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2 bg-white border-pink-200 text-zinc-900" />
                </div>
              </div>
              <div className="mt-6 flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)} className="border-pink-300 text-zinc-700 rounded-full">{t("back")}</Button>
                <Button onClick={submitBooking} disabled={submitting} data-testid="step2-continue-btn" className="gold-gradient text-white font-semibold rounded-full px-8">
                  {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : null} {t("continue")}
                </Button>
              </div>
            </div>
          )}

          {step === 3 && payment && (
            <div className="bg-card border border-pink-200 rounded-2xl p-6 sm:p-8">
              <h2 className="font-display text-2xl text-zinc-900">{t("payment")}</h2>
              <p className="text-sm text-zinc-600 mt-2">Scan the QR or pay to the UPI ID below. Pay exactly <span className="text-gold font-semibold">{fmtINR(payment.advance_amount)}</span>, then enter your UPI reference number.</p>

              <div className="mt-6 grid sm:grid-cols-2 gap-6 items-start">
                <div className="bg-white rounded-2xl p-4 flex flex-col items-center">
                  <img src={payment.qr_url} alt="UPI QR" className="w-full max-w-[260px] rounded-lg" data-testid="upi-qr-image" />
                </div>
                <div className="space-y-4">
                  <div className="rounded-xl bg-white border border-pink-200 p-4">
                    <span className="text-xs text-zinc-500">UPI ID</span>
                    <div className="flex items-center justify-between gap-2 mt-1">
                      <span className="text-gold font-mono text-sm" data-testid="upi-id">{payment.upi_id}</span>
                      <button onClick={() => { navigator.clipboard.writeText(payment.upi_id); toast.success("UPI ID copied"); }} className="text-zinc-600 hover:text-gold" data-testid="copy-upi-btn"><Copy size={15} /></button>
                    </div>
                  </div>
                  <a href={payment.upi_link} data-testid="upi-app-link">
                    <Button variant="outline" className="w-full border-gold/40 text-gold hover:bg-gold/10"><Smartphone size={16} className="mr-2" /> Pay via UPI App</Button>
                  </a>
                  <div>
                    <Label className="text-zinc-700">UPI Reference / Transaction ID *</Label>
                    <Input data-testid="utr-input" value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="e.g. 4312XXXXXXXX" className="mt-2 bg-white border-pink-200 text-zinc-900" />
                  </div>
                  <Button onClick={submitRef} disabled={submitting} data-testid="submit-payment-ref-btn" className="w-full gold-gradient text-white font-semibold rounded-full">
                    {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : null} Submit Payment Reference
                  </Button>
                  <p className="text-[11px] text-zinc-500">Your slot confirms once we verify the payment (usually within a few hours).</p>
                </div>
              </div>
            </div>
          )}

          {step === 4 && payment && (
            <div className="bg-card border border-gold/30 rounded-2xl p-8 text-center" data-testid="booking-confirmation">
              <div className="h-16 w-16 rounded-full gold-gradient flex items-center justify-center mx-auto"><PartyPopper size={30} className="text-black" /></div>
              <h2 className="font-display text-3xl text-zinc-900 mt-5">Thank You!</h2>
              <p className="text-zinc-700 mt-3">Your booking request is received and payment is being verified.</p>
              <div className="mt-6 inline-block rounded-xl bg-white border border-pink-200 px-6 py-4">
                <span className="text-xs text-zinc-500">{t("booking_number")}</span>
                <div className="text-2xl font-display gold-text" data-testid="confirmation-booking-number">{payment.booking_number}</div>
              </div>
              <p className="text-sm text-zinc-500 mt-4">Save this number to track your booking.</p>
              <div className="mt-7 flex flex-wrap justify-center gap-3">
                <Button onClick={() => navigate("/track")} className="gold-gradient text-white font-semibold rounded-full px-7">{t("nav_track")}</Button>
                <Button variant="outline" onClick={() => navigate("/")} className="border-pink-300 text-zinc-700 rounded-full">{t("nav_home")}</Button>
              </div>
            </div>
          )}
        </div>

        {/* Summary */}
        <aside className="bg-card border border-pink-200 rounded-2xl p-6 h-fit lg:sticky lg:top-24">
          <h3 className="text-xs tracking-[0.2em] uppercase text-gold">{t("booking_summary")}</h3>
          <img src={pkgImage(pkg)} alt="" className="w-full h-32 object-cover rounded-xl mt-4" />
          <h4 className="font-display text-lg text-zinc-900 mt-4">{pick(pkg, lang, "name")}</h4>
          {date && <p className="text-sm text-zinc-600 mt-1">{t("event_date")}: {date}</p>}
          <div className="mt-5 space-y-2.5 text-sm border-t border-pink-200 pt-4">
            <div className="flex justify-between"><span className="text-zinc-600">{t("package_price")}</span><span className="text-zinc-900">{fmtINR(pkg.price)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-600">{t("advance_10")}</span><span className="text-gold font-semibold">{fmtINR(advance)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-600">{t("balance_due")}</span><span className="text-zinc-900">{fmtINR(balance)}</span></div>
          </div>
          {pkg.customization_extra && <p className="text-[11px] text-amber-500/90 mt-3">{t("customization_extra")}</p>}
          <p className="text-[11px] text-zinc-500 mt-4 leading-relaxed">{t("refund_note")}</p>
        </aside>
      </div>
    </div>
  );
}
